import { useState, useEffect } from 'react';
import { takeSessionEndedMessage } from '../utils/sessionEnded';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { api } from '../api/client';
import aideyLogo from '../assets/sidebar-logo.svg';
import { messageFromError } from '../utils/apiError';   // Block 233
import { showErrorToast } from '../utils/toast';        // Block 233

// ─── Shared style tokens ──────────────────────────────────────────────────────
const NAVY = '#0C2D48';
const YELLOW = '#E4E69B';
const BORDER = '#35454C';

const inputStyle = {
  display: 'block', width: '100%', padding: '0.7rem 0.9rem',
  border: '1px solid #c5cdd4', borderRadius: 8, fontSize: '0.95rem',
  fontFamily: 'Raleway, sans-serif', boxSizing: 'border-box',
  outline: 'none', transition: 'border-color 0.15s', background: '#fff',
};

const labelStyle = {
  display: 'block', fontSize: '0.82rem', fontWeight: 700,
  color: BORDER, marginBottom: '0.35rem', fontFamily: 'Poppins, sans-serif',
};

const errStyle = {
  background: '#fde8e8', border: '1px solid #f5a5a5', borderRadius: 8,
  padding: '0.65rem 0.9rem', fontSize: '0.85rem', color: '#A12710',
  marginBottom: '1rem', fontFamily: 'Raleway, sans-serif',
};

// ─── Main login screen ────────────────────────────────────────────────────────
// Guard against any browser-native validation messages leaking into the UI
function sanitizeError(msg) {
  if (!msg) return 'Sign-in failed. Please try again.';
  const lower = msg.toLowerCase();
  if (lower.includes('pattern') || lower.includes('did not match') || lower.includes('constraint')) {
    return 'Please enter a valid email address.';
  }
  return msg;
}

export default function Login() {
  const { login } = useCurrentUser();
  const { t } = useTranslation('common');
  // Tier 1 #9: the invitation pages send people here with their address filled in.
  const params = (() => { try { return new URLSearchParams(window.location.search); } catch { return new URLSearchParams(); } })();
  const [email, setEmail] = useState(() => params.get('email') || '');
  const joining = params.get('joining') === '1';
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [trusted, setTrusted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendInfo, setResendInfo] = useState('');

  // Clear any stale error from a previous session or browser autofill quirk — and then
  // say so if the person is here because their session ENDED rather than because they
  // chose to sign in. They arrive mid-action with their work gone; the one thing they need
  // is to know it was not saved. takeSessionEndedMessage reads once and clears.
  useEffect(() => {
    const ended = takeSessionEndedMessage();
    setError(ended || null);
  }, []);

  async function handleResend() {
    setResendInfo('');
    try {
      await api('/api/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
    } catch (err) { showErrorToast(messageFromError(err, "Could not resend that email.")); }
    setResendInfo('Verification email sent. Please check your inbox.');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setNeedsVerification(false);
    setResendInfo('');
    if (!email.trim()) return setError('Please enter your email or username.');
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password, trusted);
    } catch (err) {
      if (err?.body?.code === 'EMAIL_NOT_VERIFIED' || err?.code === 'EMAIL_NOT_VERIFIED') {
        setNeedsVerification(true);
        setError('Please verify your email address before signing in. Check your inbox for the verification link.');
      } else {
        setError(sanitizeError(err.message));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(150deg, ${NAVY} 0%, #1a4a7a 50%, #0a2038 100%)`,
      padding: '1rem', fontFamily: 'Raleway, sans-serif',
    }}>
      <div style={{
        background: '#f5f7f9',
        border: `1px solid ${BORDER}`,
        borderRadius: 16,
        width: '100%',
        maxWidth: 420,
        boxShadow: '0 12px 48px rgba(12,45,72,0.28), 0 2px 8px rgba(12,45,72,0.10)',
      }}>

        {/* Logo header */}
        <div style={{ padding: '2rem 2rem 1.25rem', textAlign: 'center' }}>
          <img
            src={aideyLogo}
            alt="Aidey"
            style={{
              height: 52, width: 'auto', display: 'block', margin: '0 auto',
              border: 'none', outline: 'none', boxShadow: 'none', background: 'transparent',
            }}
          />
          <p style={{
            margin: '0.75rem 0 0', fontSize: '0.84rem', color: '#4a5a5b',
            fontFamily: 'Raleway, sans-serif', lineHeight: 1.4,
          }}>
            {t('auth.tagline')}
          </p>
        </div>

        <div style={{ borderTop: `1px solid #c5cdd4`, margin: '0 1.5rem' }} />

        {/* Form */}
        <div style={{ padding: '1.75rem 2rem 2rem' }}>
          <h2 style={{
            margin: '0 0 1.25rem', fontSize: '1.15rem', color: NAVY,
            fontFamily: 'Poppins, sans-serif', fontWeight: 700,
          }}>
            {t('auth.sign_in')}
          </h2>

          <form onSubmit={handleSubmit} noValidate>
            {error && <div style={errStyle}>⚠ {error}</div>}
            {joining && !error && (
              <div data-testid="joining-note" style={{ background: '#eaf6ef', border: '1px solid #6abf85', color: '#1e6645', borderRadius: 8, padding: '0.75rem 0.9rem', fontSize: '0.88rem', marginBottom: '1rem' }}>
                Your account is ready. Sign in to join your team.
              </div>
            )}
            {needsVerification && !resendInfo && (
              <div style={{ margin: '0 0 0.75rem', fontSize: '0.85rem' }}>
                <button type="button" onClick={handleResend}
                  style={{ background: 'none', border: 'none', color: NAVY, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>
                  Resend verification email
                </button>
              </div>
            )}
            {resendInfo && <div style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#166534' }}>{resendInfo}</div>}

            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="login-email" style={labelStyle}>Email or username</label>
              <input
                id="login-email" name="email"
                type="text" value={email}
                onChange={e => { setEmail(e.target.value); if (error) setError(null); }}
                placeholder="you@example.com or username"
                style={inputStyle}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck="false"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="login-password" style={labelStyle}>{t('auth.password_label')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="login-password" name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => { setPassword(e.target.value); if (error) setError(null); }}
                  placeholder="••••••••"
                  style={{ ...inputStyle, paddingRight: '2.5rem' }}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: '0.6rem', top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#94a3b8', fontSize: '1.05rem', padding: '0.25rem',
                    lineHeight: 1, display: 'flex', alignItems: 'center',
                  }}
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
              <div style={{ marginTop: '0.45rem', textAlign: 'right' }}>
                <a
                  href="/forgot-password"
                  style={{
                    color: NAVY, fontWeight: 600, fontSize: '0.8rem',
                    textDecoration: 'none', fontFamily: 'Raleway, sans-serif',
                  }}
                >
                  {t('auth.forgot_password')}
                </a>
              </div>
            </div>

            <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
              <input
                type="checkbox"
                id="trust-device"
                checked={trusted}
                onChange={e => setTrusted(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: NAVY, cursor: 'pointer', flexShrink: 0 }}
              />
              <label htmlFor="trust-device" style={{
                fontSize: '0.83rem', color: '#4a5a5b',
                fontFamily: 'Raleway, sans-serif', cursor: 'pointer',
                userSelect: 'none', margin: 0,
              }}>
                Trust this device for 30 days
              </label>
            </div>

            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '0.75rem',
              background: loading ? '#c8cabb' : YELLOW,
              border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '1rem',
              fontFamily: 'Poppins, sans-serif', cursor: loading ? 'not-allowed' : 'pointer',
              color: '#242629', transition: 'background 0.2s',
            }}>
              {loading ? t('auth.signing_in') : t('auth.sign_in')}
            </button>

            <p style={{
              textAlign: 'center', marginTop: '1.1rem', fontSize: '0.82rem',
              color: '#4a5a5b', lineHeight: 1.6, fontFamily: 'Raleway, sans-serif',
            }}>
              {t('auth.no_account')}{' '}
              <a href="/register" style={{
                color: NAVY, fontWeight: 700, textDecoration: 'none',
                fontFamily: 'Raleway, sans-serif',
              }}>
                {t('auth.create_account')}
              </a>
            </p>
          </form>
        </div>

      </div>
      <div style={{ position: 'fixed', bottom: 8, right: 12, pointerEvents: 'none' }}>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace', opacity: 0.4 }}>
          build {((process.env.REACT_APP_BUILD_SHA || '').slice(0, 7)) || 'dev'}
        </span>
      </div>
    </div>
  );
}
